import React from "react";
import cx from "classnames";
import { useTranslation } from "i18n";
import { StarIcon } from "assets";
import ProfileCard from "pages/Profile/components/common/ProfileCard";
import { useGetSkills } from "pages/Profile/hooks/useGetSkills";
import { SKILLS_FULL_PATH } from "shared/constants";
import NoDataMessage from "pages/Profile/components/common/NoDataMessage";
import { usePublicView } from "pages/Profile/hooks/usePublicView";
import { dataTestIds } from "data-testids";

interface Props {
  isEdit?: boolean;
}

const SkillsSection = ({ isEdit }: Props) => {
  const { t } = useTranslation();
  const { isPublicView } = usePublicView();
  const { skills, isLoading } = useGetSkills();

  const isSkillsSectionHidden = !skills.length && !isEdit;
  const isNoData = !skills.length && !isPublicView;

  if (isSkillsSectionHidden) {
    return null;
  }

  return (
    <ProfileCard
      className={cx("grid grid-cols-1 gap-4", {
        "min-h-[141px]": isNoData,
      })}
      isEdit={isEdit}
      isLoading={isLoading}
      editToPath={SKILLS_FULL_PATH}
      data-testid={dataTestIds.profile["Profile.ProfileCard.skillSection"]}
    >
      {isNoData && (
        <NoDataMessage
          category={t("performer:profile.skills.noData.category")}
          content={t("performer:profile.skills.noData.content")}
        />
      )}

      {skills.map(([category, skillItems]) => {
        return (
          <div
            key={category}
            data-testid={
              dataTestIds.profile["Profile.ProfileCard.skillsCategoryGroup"]
            }
          >
            <h3 className="mb-3 text-s uppercase tracking-wider dark:text-white">
              {category}
            </h3>
            <div className="flex flex-wrap gap-2">
              {skillItems.map(([name, isFeatured]) => (
                <span
                  key={name}
                  className="flex items-center gap-1 rounded-lg border border-white/20 bg-white/15 px-3 py-1 text-xs font-medium"
                  data-testid={
                    dataTestIds.profile["Profile.ProfileCard.skillCategoryItem"]
                  }
                >
                  {isFeatured && (
                    <StarIcon className="min-h-3 min-w-3 text-yellow-golden" />
                  )}
                  {name}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </ProfileCard>
  );
};

export default SkillsSection;
